import axios, {AxiosError, type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig} from 'axios';

// In production, empty string means use same origin (no base URL needed)
const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL === '' ? '' : import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

class ApiClient {
  private instance: AxiosInstance;
  private token: string | null = null;
    private refreshingPromise: Promise<string | null> | null = null;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  public setAuthToken(token: string) {
    this.token = token;
      // Standardize on 'auth_token' key across app
      localStorage.setItem('auth_token', token);
  }

  public clearAuthToken() {
    this.token = null;
      localStorage.removeItem('auth_token');
  }

  public loadAuthToken() {
      const token = localStorage.getItem('auth_token');
    if (token) {
      this.token = token;
    }
  }

  // HTTP methods
  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<T>(url, config);
    return response.data;
  }

  public async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<T>(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<T>(url, data, config);
    return response.data;
  }

  public async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.patch<T>(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete<T>(url, config);
    return response.data;
  }

  private setupInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Add auth token if available
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }

        // Add correlation ID for tracking
        config.headers['X-Correlation-ID'] = this.generateCorrelationId();

        return config;
      },
      (error: AxiosError) => {
        console.error('[API Request Error]', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError) => {
          const originalRequest = error.config as AxiosRequestConfig & { _retryCount?: number };

        // Handle 401 Unauthorized
          if (error.response?.status === 401) {
              // Avoid trying to refresh for the refresh endpoint itself
              const isRefreshCall = (originalRequest.url || '').includes('/api/auth/refresh');
              if (isRefreshCall) {
                  return Promise.reject(error);
              }

              originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

              // Attempt token refresh with up to 3 tries, shared across concurrent 401s
              try {
                  const newToken = await this.refreshSessionTokenWithRetry(3);
                  if (newToken) {
                      // Update header and retry original request
                      if (originalRequest.headers) {
                          (originalRequest.headers as any)['Authorization'] = `Bearer ${newToken}`;
                      } else {
                          originalRequest.headers = {Authorization: `Bearer ${newToken}`} as any;
                      }
                      return this.instance.request(originalRequest);
                  }
              } catch (refreshError) {
                  // fallthrough to redirect below
              }

              // Refresh failed or no token available, redirect to login
              this.clearAuthToken();
              localStorage.removeItem('refresh_token');
              if (window.location.pathname !== '/login') {
                  window.location.href = '/login';
              }
              return Promise.reject(error);
        }

        // Handle network errors
        if (!error.response) {
          console.error('[API Network Error]', error.message);
          throw new Error('Network error. Please check your connection.');
        }

        // Handle other errors
        console.error('[API Response Error]', {
          url: error.config?.url,
          status: error.response?.status,
          data: error.response?.data,
        });

        throw error;
      }
    );
  }

    private async refreshSessionTokenWithRetry(maxAttempts: number): Promise<string | null> {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) return null;

        // If a refresh is already in progress, reuse it
        if (this.refreshingPromise) {
            return this.refreshingPromise;
        }

        let attempt = 0;
        const doRefresh = async (): Promise<string | null> => {
            while (attempt < maxAttempts) {
                attempt += 1;
                try {
                    const resp = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {refresh_token: refreshToken});
                    const {access_token, refresh_token: newRefresh} = resp.data as {
                        access_token: string;
                        refresh_token?: string;
                    };
                    if (access_token) {
                        // Persist both tokens and update in-memory token
                        this.setAuthToken(access_token);
                        if (newRefresh) {
                            localStorage.setItem('refresh_token', newRefresh);
                        }
                        return access_token;
                    }
                    // If no access token returned, break to avoid infinite loop
                    break;
                } catch (err: any) {
                    const status = err?.response?.status;
                    // For invalid/expired refresh token, don't retry
                    if (status === 400 || status === 401 || status === 403) {
                        break;
                    }
                    // Backoff before next attempt
                    await new Promise((res) => setTimeout(res, 250 * attempt));
                }
            }
            return null;
        };

        this.refreshingPromise = doRefresh().finally(() => {
            this.refreshingPromise = null;
        });

        return this.refreshingPromise;
    }

  private generateCorrelationId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Create singleton instance
const apiClient = new ApiClient();

// Load auth token on initialization
apiClient.loadAuthToken();

export default apiClient;
